import { Button } from "@heroui/react";
import { Pencil } from "lucide-react";
import { useMemo, useState } from "react";

import { MarginEditForm } from "../modal/edit";

import {
  DataTable,
  useClientTable,
  type DataGridColumn,
} from "@/components/kit";
import { useListMargins } from "@/hooks/api/margin/useListMargins";
import { Margin } from "@/types/margin";
import { formatDateTime } from "@/utils/date";

const money = (value: number | null) => (value ? `AED ${value}` : "N/A");

export const MarginTable = () => {
  const { data: margins, isLoading: isLoadingMargins } = useListMargins();
  const [selectedMargin, setSelectedMargin] = useState<Margin | null>(null);
  const list = (margins as Margin[] | undefined) ?? [];

  const columns = useMemo<DataGridColumn<Margin>[]>(
    () => [
      {
        id: "priceFrom",
        header: "Price from",
        isRowHeader: true,
        allowsSorting: true,
        sortFn: (a, b) => (a.priceFrom ?? 0) - (b.priceFrom ?? 0),
        cell: (margin) => (
          <span className="font-medium tabular-nums">
            {money(margin.priceFrom)}
          </span>
        ),
      },
      {
        id: "priceTo",
        header: "Price to",
        allowsSorting: true,
        sortFn: (a, b) => (a.priceTo ?? Infinity) - (b.priceTo ?? Infinity),
        cell: (margin) => (
          <span className="tabular-nums">{money(margin.priceTo)}</span>
        ),
      },
      {
        id: "amount",
        header: "Applied margin",
        align: "end",
        allowsSorting: true,
        sortFn: (a, b) => Number(a.amount) - Number(b.amount),
        cell: (margin) => (
          <span className="font-medium tabular-nums">
            {margin.amount ? `AED ${margin.amount}` : "N/A"}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: "Created",
        allowsSorting: true,
        sortFn: (a, b) => a.createdAt.localeCompare(b.createdAt),
        cell: (margin) => (
          <span className="text-muted tabular-nums">
            {formatDateTime(margin.createdAt)}
          </span>
        ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (margin) => (
          <Button
            isIconOnly
            aria-label="Edit margin"
            size="sm"
            variant="ghost"
            onPress={() => setSelectedMargin(margin)}
          >
            <Pencil className="size-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  const table = useClientTable(list, {
    columns,
    initialSort: { column: "priceFrom", direction: "ascending" },
    pageSize: 50,
  });

  return (
    <div className="flex min-h-0 max-w-4xl flex-1 flex-col">
      <DataTable
        compact
        columns={columns}
        empty={{ title: "No margins configured" }}
        getRowId={(margin) => margin.id}
        isLoading={isLoadingMargins && list.length === 0}
        label="Margins"
        rows={table.rows}
        sort={table.sort}
        onSortChange={table.onSortChange}
      />

      <MarginEditForm
        isOpen={Boolean(selectedMargin)}
        margin={selectedMargin}
        onClose={() => setSelectedMargin(null)}
      />
    </div>
  );
};
